<template>
  <image 
    :src="realSrc" 
    :mode="mode" 
    :class="className" 
    :style="customStyle"
    :lazy-load="lazyLoad"
    @error="handleError"
    @click="handleClick"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { IMG_BASE_URL } from '@/utils/request';

// 定义 Props
const props = defineProps({
  src: { 
    type: String, 
    default: '' 
  },
  mode: { 
    type: String, 
    default: 'scaleToFill' 
  },
  // 默认兜底图，可以根据项目情况修改
  defaultSrc: { 
    type: String, 
    default: '/static/logo.png' 
  }, 
  // 接受 class 属性透传 (Vue 默认会自动透传 class 到根元素，但为了保险显式接收)
  className: { 
    type: String, 
    default: '' 
  },
  customStyle: { 
    type: [Object, String], 
    default: '' 
  },
  lazyLoad: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['click', 'error']);

const isError = ref(false);

// 监听 src 变化，重置错误状态
watch(() => props.src, () => {
  isError.value = false;
});

const realSrc = computed(() => {
  // 1. 如果加载出错了，显示默认图
  if (isError.value) return props.defaultSrc;
  
  // 2. 如果 src 为空，显示默认图
  if (!props.src) return props.defaultSrc;
  
  const src = props.src;
  
  // 3. 绝对路径 (http/https) 或 Base64 (data:image) -> 直接返回
  if (src.startsWith('http') || src.startsWith('//') || src.startsWith('data:image')) {
    return src;
  }
  
  // 4. 本地静态资源 (/static) -> 直接返回
  if (src.startsWith('/static')) {
    return src;
  }
  
  // 5. 后端相对路径 -> 拼接域名
  // 注意：IMG_BASE_URL 可能末尾带斜杠，src 可能开头带斜杠，需要处理避免双斜杠，或者简单拼接交给浏览器容错
  // 这里 request.ts 里的 IMG_BASE_URL 通常是不带斜杠的 (http://domain)，而 src 通常带斜杠 (/uploads/...)
  // 简单拼接即可
  return IMG_BASE_URL + src;
});

const handleError = (e: any) => {
  // console.warn('Image Load Error:', props.src);
  isError.value = true;
  emit('error', e);
};

const handleClick = (e: any) => {
  emit('click', e);
};
</script>

<style scoped>
/* 可以在这里添加一些默认样式，比如加载中的占位背景 */
</style>